import { useEffect, useRef, useState } from "react";

const FRAME_MS = 33;

function prefersReducedMotion() {
  return typeof window !== "undefined" &&
    !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Streams arrive in uneven bursts. This reveals `target` at a steady pace
 * that speeds up as the backlog grows, so text flows instead of jumping, and
 * never trails a finished response by more than a few frames.
 */
export function useSmoothText(target: string, streaming: boolean): string {
  const [shown, setShown] = useState(target);
  const shownRef = useRef(target);
  const targetRef = useRef(target);
  const streamingRef = useRef(streaming);
  const rafRef = useRef<number | null>(null);
  const lastTickRef = useRef(0);

  targetRef.current = target;
  streamingRef.current = streaming;

  useEffect(() => {
    // Text that was not produced by appending (a reset, a loaded transcript)
    // has nothing to animate toward.
    if (!target.startsWith(shownRef.current) || prefersReducedMotion()) {
      shownRef.current = target;
      setShown(target);
      return;
    }
    if (rafRef.current !== null || shownRef.current.length >= target.length) return;

    const tick = (now: number) => {
      if (now - lastTickRef.current < FRAME_MS) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      lastTickRef.current = now;
      const goal = targetRef.current;
      const current = shownRef.current;
      const backlog = goal.length - current.length;
      if (backlog <= 0 || !goal.startsWith(current)) {
        shownRef.current = goal;
        setShown(goal);
        rafRef.current = null;
        return;
      }
      // Live streams drain about a seventh of the backlog per frame; once the
      // response is complete, finish within roughly a quarter second.
      const share = streamingRef.current ? 7 : 3;
      const step = Math.min(backlog, Math.max(2, Math.ceil(backlog / share)));
      let end = current.length + step;
      const space = goal.indexOf(" ", end);
      if (space !== -1 && space - end < 8) end = space;
      const next = goal.slice(0, end);
      shownRef.current = next;
      setShown(next);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [target]);

  useEffect(() => () => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
  }, []);

  return shown;
}
